import axios from 'axios'
import http from '@/api/http'
import keycloakConfig from '@/config/keycloak_config'
import { ErrorCode } from '@/errors/codes'
import type { UserInfo, UserInfoResponse } from '@/models/user_info'

export type { UserInfo, UserInfoResponse } from '@/models/user_info'

interface FetchUserInfoRequest {
  // JSON body for POST `/auth/userinfo` (backend `TokensReqDTO`, snake_case).

  auth_code: string
  // OAuth 2.0 authorization code from the IdP callback.

  redirect_uri: string
  // Registered redirect URI for this client (must match the login request).

  nonce: string
  // OIDC nonce stored before redirect; BFF validates it against the ID token.
}

export async function fetchUserInfo(secrets: {
  authCode: string
  nonce: string
}): Promise<UserInfoResponse> {
  const { authCode, nonce } = secrets
  const redirectUri = keycloakConfig.redirectUri

  // Backend `TokensReqDTO`: snake_case `auth_code`, `redirect_uri`, `nonce` (all required).
  if (!authCode) {
    throw new Error('Missing authorization code for token exchange')
  }
  if (!redirectUri) {
    throw new Error('Missing redirect_uri for token exchange')
  }

  const request: FetchUserInfoRequest = {
    auth_code: authCode,
    redirect_uri: redirectUri,
    nonce,
  }

  const { data } = await http.post<UserInfoResponse>('/auth/userinfo', request)
  return data
}

/**
 * Probe the BFF for the currently signed-in user using the session cookies (XAUTH-DATA + XSRF-TOKEN).
 *
 * Sends `GET /auth/me` and returns the bare {@link UserInfo} payload on success. The endpoint requires
 * an authenticated session — when the session cookie is missing or unusable the BFF answers with
 * HTTP 401 + `error_code = BACKEND_10030` (`SESSION_NOT_AUTHENTICATED`); the caller should treat that
 * as the normal "not logged in yet" case rather than a hard error.
 *
 * Uses `skipAuthRedirect: true` so the global response interceptor does not navigate to a dedicated
 * 401 error page on this call. This is intended for the Welcome tab's "is the user already signed in
 * from a previous session?" check, where landing on an error page would be wrong.
 *
 * @returns the {@link UserInfo} payload on HTTP 200; `null` when the BFF reports the session as not
 *   authenticated (`SESSION_NOT_AUTHENTICATED`) or any 401, so the caller can simply render the
 *   logged-out UI. Other errors (network failure, 5xx, etc.) are re-thrown.
 */
export async function fetchCurrentUser(): Promise<UserInfo | null> {
  try {
    const { data } = await http.get<UserInfo>('/auth/me', { skipAuthRedirect: true })
    return data
  } catch (e) {
    if (axios.isAxiosError(e)) {
      const status = e.response?.status
      const payload = e.response?.data as { error_code?: unknown } | undefined
      const code = typeof payload?.error_code === 'string' ? payload.error_code : undefined
      if (status === 401 || code === ErrorCode.SessionNotAuthenticated) {
        return null
      }
    }
    throw e
  }
}

/**
 * End the current session: `POST /auth/logout`. The BFF revokes the refresh token at
 * Keycloak and clears the XAUTH-DATA / XSRF-TOKEN cookies.
 *
 * Uses `skipAuthRedirect: true` (same reasoning as {@link fetchCurrentUser}) so a 401 —
 * the session was already gone — does not trigger the global unauthorized-page redirect;
 * the caller treats that the same as a successful logout.
 */
export async function logout(): Promise<void> {
  try {
    await http.post('/auth/logout', undefined, { skipAuthRedirect: true })
  } catch (e) {
    if (axios.isAxiosError(e) && e.response?.status === 401) {
      return
    }
    throw e
  }
}
